import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort, hours, pct, daysUntil, isoDate } from "../format.js";
import {
  Async, Badge, BarList, Button, Card, Check, Empty, ErrorPanel, Field, Fields, Grid, Input, KPI,
  Modal, Select, Table, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

/** Drafts and voided invoices aren't money billed to the client yet. */
const isBilled = (i) => i.display_status !== "draft" && i.display_status !== "void";

export default function ProjectDetail({ id }) {
  const { settings, reloadApp, me } = useApp();
  const canManage = !!me.permissions["projects.manage"];
  const canInvoice = !!me.permissions["invoices.manage"];
  const state = useAsync(() => api.get(`projects/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const sym = settings.currency_symbol;

  return (
    <>
      <TopBar crumb="Track work / Projects" title={state.data?.project?.name || "Project"}>
        {canManage && (
          <Button onClick={() => setEditing(true)} disabled={!state.data}>
            Edit
          </Button>
        )}
        {canInvoice && (
          <Button kind="pri" onClick={() => navigate("/invoices/new")}>
            ＋ Invoice project
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(d) => {
            const { project, byUser, expenses, invoices, time, stats } = d;
            const loggedHours = Number(stats?.hours || 0);
            const billed = invoices.filter(isBilled).reduce((s, i) => s + Number(i.total || 0), 0);
            const used =
              project.billing_type === "hourly"
                ? pct(loggedHours, project.budget_hours)
                : pct(billed, project.budget_amount);
            const left = project.due_on ? daysUntil(project.due_on) : null;
            const totalMinutes = time.reduce((s, t) => s + Number(t.minutes || 0), 0);

            return (
              <>
                <Grid cols={4}>
                  <KPI
                    label="Hours logged"
                    value={loggedHours.toFixed(1)}
                    sub={
                      project.billing_type === "hourly"
                        ? `of ${Number(project.budget_hours || 0).toFixed(0)} budgeted`
                        : "time & materials tracked"
                    }
                  />
                  <KPI
                    label="Budget used"
                    value={`${used}%`}
                    tone={used > 85 ? "warn" : undefined}
                    sub={
                      project.billing_type === "hourly"
                        ? `${money(Number(project.budget_hours || 0) * Number(project.hourly_rate || 0), sym)} budget`
                        : `${money(project.budget_amount, sym)} flat fee`
                    }
                  />
                  <KPI
                    label="Billed to date"
                    value={money(billed, sym)}
                    sub={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`}
                  />
                  <KPI
                    label="Deadline"
                    value={fmtDateShort(project.due_on)}
                    tone={left !== null && left < 14 ? "danger" : undefined}
                    sub={left === null ? "no due date set" : left > 0 ? `${left} days left` : "Past due"}
                  />
                </Grid>

                <Grid cols="1fr 1fr" className="mt" style={{ alignItems: "start" }}>
                  <Card title="Time by person">
                    {byUser.length ? (
                      <BarList
                        rows={byUser.map((u) => ({ label: u.name, value: u.minutes }))}
                        format={(m) => `${hours(m)} h`}
                      />
                    ) : (
                      <Empty icon="◷">No time logged yet</Empty>
                    )}
                  </Card>

                  <Card
                    bare
                    title="Billable expenses"
                    right={
                      <Button sm kind="gho" onClick={() => navigate("/expenses")}>
                        All →
                      </Button>
                    }
                  >
                    <Table
                      rows={expenses}
                      empty={<Empty icon="◰">No expenses on this project</Empty>}
                      columns={[
                        {
                          key: "spent_on",
                          label: "Date",
                          width: 90,
                          render: (x) => <span className="sm dim">{fmtDateShort(x.spent_on)}</span>,
                        },
                        {
                          key: "vendor",
                          label: "Vendor",
                          render: (x) => (
                            <>
                              <span className="sm">{x.vendor || "—"}</span>
                              <div className="tiny muted">{x.category}</div>
                            </>
                          ),
                        },
                        {
                          key: "amount",
                          label: "Amount",
                          align: "right",
                          render: (x) => <span className="num">{money(x.amount, sym)}</span>,
                        },
                        {
                          key: "rebill",
                          label: "",
                          align: "right",
                          render: (x) =>
                            x.billable ? (
                              <Badge status="unbilled" label="Rebill" />
                            ) : (
                              <Badge status="draft" label="Absorbed" />
                            ),
                        },
                      ]}
                    />
                  </Card>
                </Grid>

                <Card className="mt" bare title="Invoices">
                  <Table
                    rows={invoices}
                    onRowClick={(i) => navigate(`/invoices/${i.id}`)}
                    empty={<Empty icon="▤">Nothing invoiced on this project yet</Empty>}
                    columns={[
                      { key: "number", label: "Invoice", render: (i) => <b>{i.number}</b> },
                      {
                        key: "issue_date",
                        label: "Issued",
                        render: (i) => <span className="sm dim">{fmtDateShort(i.issue_date)}</span>,
                      },
                      {
                        key: "total",
                        label: "Total",
                        align: "right",
                        render: (i) => <span className="num">{money(i.total, sym)}</span>,
                      },
                      {
                        key: "balance",
                        label: "Balance",
                        align: "right",
                        render: (i) =>
                          Number(i.balance || 0) ? (
                            <span className="num">{money(i.balance, sym)}</span>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      { key: "status", label: "Status", render: (i) => <Badge status={i.display_status} /> },
                    ]}
                  />
                </Card>

                <Card
                  className="mt"
                  bare
                  title="Time entries"
                  right={<span className="sm dim num">{hours(totalMinutes)} hours total</span>}
                >
                  <Table
                    rows={time}
                    empty={<Empty icon="◷">No time logged yet</Empty>}
                    columns={[
                      {
                        key: "entry_date",
                        label: "Date",
                        render: (t) => <span className="sm dim">{fmtDateShort(t.entry_date)}</span>,
                      },
                      { key: "user_name", label: "Who", render: (t) => <span className="sm">{t.user_name}</span> },
                      {
                        key: "item_name",
                        label: "Service",
                        render: (t) => <span className="sm dim">{t.item_name || "—"}</span>,
                      },
                      { key: "note", label: "Note", render: (t) => <span className="sm dim">{t.note || "—"}</span> },
                      {
                        key: "minutes",
                        label: "Hours",
                        align: "right",
                        render: (t) => <span className="num">{hours(t.minutes)}</span>,
                      },
                      {
                        key: "value",
                        label: "Value",
                        align: "right",
                        render: (t) =>
                          t.billable ? (
                            <span className="num">{money(t.value, sym)}</span>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      {
                        key: "status",
                        label: "Status",
                        render: (t) =>
                          t.invoice_id ? (
                            <Badge status="billed" />
                          ) : t.billable ? (
                            <Badge status="unbilled" />
                          ) : (
                            <Badge status="internal" />
                          ),
                      },
                    ]}
                  />
                </Card>
              </>
            );
          }}
        </Async>
      </Wrap>

      {editing && state.data && (
        <EditProjectModal
          project={state.data.project}
          members={state.data.members}
          onClose={() => setEditing(false)}
          onSaved={() => {
            state.reload();
            reloadApp();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- edit modal */

function EditProjectModal({ project, members, onClose, onSaved }) {
  const { clients, users } = useApp();
  const toast = useToast();
  const team = users.filter((u) => u.status !== "disabled");

  const [form, setForm] = useState({
    client_id: project.client_id || "",
    name: project.name || "",
    billing_type: project.billing_type || "hourly",
    hourly_rate: project.hourly_rate ?? "",
    budget_hours: project.budget_hours ?? "",
    budget_amount: project.budget_amount ?? "",
    starts_on: isoDate(project.starts_on),
    due_on: isoDate(project.due_on),
    status: project.status || "active",
  });
  const [memberIds, setMemberIds] = useState(members.map((m) => m.id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = (id) =>
    setMemberIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await api.put(`projects/${project.id}`, {
        client_id: form.client_id,
        name: form.name,
        billing_type: form.billing_type,
        hourly_rate: Number(form.hourly_rate) || 0,
        budget_hours: Number(form.budget_hours) || 0,
        budget_amount: Number(form.budget_amount) || 0,
        starts_on: form.starts_on || null,
        due_on: form.due_on || null,
        status: form.status,
        member_ids: memberIds,
      });
      toast.push("Project saved");
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Edit project"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            Save changes
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Fields cols={2}>
          <Field label="Client">
            <Select value={form.client_id} onChange={(e) => set("client_id", e.target.value)}>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Project name">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
          </Field>
        </Fields>

        <Fields cols={3}>
          <Field label="Billing">
            <Select value={form.billing_type} onChange={(e) => set("billing_type", e.target.value)}>
              <option value="hourly">Hourly</option>
              <option value="flat">Flat fee</option>
            </Select>
          </Field>
          {form.billing_type === "hourly" ? (
            <>
              <Field label="Hourly rate">
                <Input value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} />
              </Field>
              <Field label="Budget hours">
                <Input value={form.budget_hours} onChange={(e) => set("budget_hours", e.target.value)} />
              </Field>
            </>
          ) : (
            <Field label="Flat fee">
              <Input value={form.budget_amount} onChange={(e) => set("budget_amount", e.target.value)} />
            </Field>
          )}
        </Fields>

        <Fields cols={3}>
          <Field label="Starts">
            <Input type="date" value={form.starts_on} onChange={(e) => set("starts_on", e.target.value)} />
          </Field>
          <Field label="Due">
            <Input type="date" value={form.due_on} onChange={(e) => set("due_on", e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="complete">Complete</option>
              <option value="archived">Archived</option>
            </Select>
          </Field>
        </Fields>

        <Field label="Team on this project">
          <div className="fields f2" style={{ gap: 8 }}>
            {team.map((u) => (
              <Check key={u.id} label={u.name} checked={memberIds.includes(u.id)} onChange={() => toggle(u.id)} />
            ))}
          </div>
        </Field>
      </Fields>
    </Modal>
  );
}
